// Поиск по документации на клиенте. Пока тексты статей не загружены, ищем по
// названиям, описаниям и ключевым словам из реестра; полный индекс по разделам
// строится, когда подгрузится src/data/docs/content.ts.

import { DOCS, docPath } from '@/data/docs/meta';
import type { DocBlock, DocContent } from '@/data/docs/types';

export interface SearchEntry {
  slug: string;
  sectionId: string | null;
  title: string;
  section: string | null;
  text: string;
  n: { title: string; section: string; keywords: string; text: string };
}

export interface SearchHit {
  entry: SearchEntry;
  href: string;
  snippet: string;
}

const MARKUP = /\*\*([^*]+)\*\*|\[([^\]]+)\]\([^)\s]+\)|`([^`]+)`/g;
const WORD_CHAR = /[a-zа-я0-9]/;

export function plainText(text: string): string {
  return text
    .replace(MARKUP, (_, label?: string, link?: string, code?: string) => label ?? link ?? code ?? '')
    .replace(/\s+/g, ' ')
    .trim();
}

/** Регистр и «ё» не важны. Длина строки не меняется, поэтому индексы совпадений годятся для исходного текста. */
export function normalize(text: string): string {
  return text.toLowerCase().replace(/ё/g, 'е');
}

/** Слова запроса без окончаний: «кассу» и «кассы» находят «касса». */
export function queryStems(query: string): string[] {
  const stems = normalize(query)
    .split(/[^a-zа-я0-9-]+/)
    .filter((w) => w.length >= 2)
    .map((w) => (w.length >= 6 ? w.slice(0, -2) : w.length === 5 ? w.slice(0, -1) : w));
  return Array.from(new Set(stems));
}

function blockText(block: DocBlock): string {
  switch (block.kind) {
    case 'p':
    case 'h3':
    case 'note':
      return block.text;
    case 'steps':
    case 'list':
      return block.items.join(' ');
    case 'table':
      return [...block.head, ...block.rows.flat()].join(' ');
  }
}

function makeEntry(
  slug: string,
  sectionId: string | null,
  title: string,
  section: string | null,
  text: string,
  keywords: string,
): SearchEntry {
  return {
    slug,
    sectionId,
    title,
    section,
    text,
    n: {
      title: normalize(title),
      section: normalize(section ?? ''),
      keywords: normalize(keywords),
      text: normalize(text),
    },
  };
}

export function buildMetaIndex(): SearchEntry[] {
  return DOCS.map((doc) => makeEntry(doc.slug, null, doc.title, null, doc.summary, doc.keywords));
}

export function buildFullIndex(content: Record<string, DocContent>): SearchEntry[] {
  const entries: SearchEntry[] = [];
  for (const doc of DOCS) {
    const article = content[doc.slug];
    entries.push(
      makeEntry(doc.slug, null, doc.title, null, plainText(`${doc.summary} ${article?.lead ?? ''}`), doc.keywords),
    );
    for (const section of article?.sections ?? []) {
      const text = plainText(section.blocks.map(blockText).join(' '));
      entries.push(makeEntry(doc.slug, section.id, doc.title, section.title, text, ''));
    }
  }
  return entries;
}

function makeSnippet(text: string, stems: string[], max = 160): string {
  const n = normalize(text);
  let at = -1;
  for (const stem of stems) {
    const i = n.indexOf(stem);
    if (i !== -1 && (at === -1 || i < at)) at = i;
  }
  if (at < 50) return text.length > max ? `${text.slice(0, max).replace(/\s+\S*$/, '')}…` : text;
  const space = text.lastIndexOf(' ', at - 40);
  const start = space === -1 ? 0 : space + 1;
  const slice = text.slice(start, start + max);
  return `…${start + max < text.length ? `${slice.replace(/\s+\S*$/, '')}…` : slice}`;
}

const MAX_PER_ARTICLE = 3;

export function searchDocs(index: SearchEntry[], query: string, limit = 8): SearchHit[] {
  const stems = queryStems(query);
  if (stems.length === 0) return [];
  const phrase = normalize(query.trim());

  const scored: Array<{ entry: SearchEntry; score: number }> = [];
  for (const entry of index) {
    let score = 0;
    for (const stem of stems) {
      const weight =
        (entry.n.title.includes(stem) ? 8 : 0) +
        (entry.n.section.includes(stem) ? 6 : 0) +
        (entry.n.keywords.includes(stem) ? 4 : 0) +
        (entry.n.text.includes(stem) ? 2 : 0);
      // Все слова запроса должны найтись, иначе «закрыть смену» выдаёт всё про смены.
      if (weight === 0) {
        score = 0;
        break;
      }
      score += weight;
    }
    if (score === 0) continue;
    if (stems.length > 1 && (entry.n.section.includes(phrase) || entry.n.text.includes(phrase))) score += 6;
    scored.push({ entry, score });
  }
  scored.sort((a, b) => b.score - a.score);

  const perArticle = new Map<string, number>();
  const hits: SearchHit[] = [];
  for (const { entry } of scored) {
    const count = perArticle.get(entry.slug) ?? 0;
    if (count >= MAX_PER_ARTICLE) continue;
    perArticle.set(entry.slug, count + 1);
    hits.push({
      entry,
      href: entry.sectionId ? `${docPath(entry.slug)}#${entry.sectionId}` : docPath(entry.slug),
      snippet: makeSnippet(entry.text, stems),
    });
    if (hits.length >= limit) break;
  }
  return hits;
}

/** Куски строки с отметкой совпадения; совпадение растягивается до конца слова. */
export function highlightParts(text: string, stems: string[]): Array<{ text: string; hit: boolean }> {
  if (stems.length === 0) return [{ text, hit: false }];
  const n = normalize(text);
  const ranges: Array<[number, number]> = [];
  for (const stem of stems) {
    let i = n.indexOf(stem);
    while (i !== -1) {
      let end = i + stem.length;
      while (end < n.length && WORD_CHAR.test(n[end])) end += 1;
      ranges.push([i, end]);
      i = n.indexOf(stem, end);
    }
  }
  if (ranges.length === 0) return [{ text, hit: false }];
  ranges.sort((a, b) => a[0] - b[0]);

  const parts: Array<{ text: string; hit: boolean }> = [];
  let cursor = 0;
  for (const [from, to] of ranges) {
    if (to <= cursor) continue;
    const start = Math.max(from, cursor);
    if (start > cursor) parts.push({ text: text.slice(cursor, start), hit: false });
    parts.push({ text: text.slice(start, to), hit: true });
    cursor = to;
  }
  if (cursor < text.length) parts.push({ text: text.slice(cursor), hit: false });
  return parts;
}
